import type { IFetchApi } from '../..'

export interface SnapshotGetter {
	snapshotFor(
		moduleScriptUrls: Array<SnapshotResource | SnapshotCreatorResource>,
		ssrWorkerCodeHash: string,
		scope: string
	): Promise<any>
}

export interface SnapshotCacheManager {
	snapshotGetterFor<T>(fetchAPI: IFetchApi, snapshotCreator: SnapshotCreator<T>): SnapshotGetter
}

export type SnapshotData<T> = {
	length(): number
	data: T
}

export enum SnapshotResourceType {
	ScriptURL = 'ScriptURL',
	ScriptCode = 'ScriptCode',
	AMDScriptURL = 'AMDScriptURL',
	AMDScriptCode = 'AMDScriptCode',
}

export type SnapshotCreatorResource = {
	type: SnapshotResourceType.ScriptCode | SnapshotResourceType.AMDScriptCode
	scriptUrl: string
	scriptCode: string
}

export type SnapshotResource = {
	type: SnapshotResourceType.ScriptURL | SnapshotResourceType.AMDScriptURL
	scriptUrl: string
}

export interface SnapshotCreator<T> {
	create(scripts: Array<SnapshotCreatorResource>): SnapshotData<T>
	createFromBuffer(data: Buffer): SnapshotData<T>
	getBuffer(data: CachedSnapshot): ArrayBuffer
}

export interface SnapshotStoreManager {
	set<T>(
		key: string,
		snapshotData: CachedSnapshot,
		snapshotCreator: SnapshotCreator<T>,
		storeMetadata?: StoreMetadata
	): void
	get<T>(key: string, snapshotCreator: SnapshotCreator<T>): Promise<Snapshot>
}

export interface SnapshotCacheStore {
	set<T>(key: string, snapshotData: CachedSnapshot, snapshotCreator?: SnapshotCreator<T>): void
	get<T>(key: string, snapshotCreator?: SnapshotCreator<T>): Promise<MaybeCachedSnapshot>
}

export type RedisKeyType = string | Buffer
export type RedisValueType = string | Buffer | number

export interface RedisClient {
	setBuffer(key: RedisKeyType, value: RedisValueType): Promise<Buffer>
	getBuffer(key: RedisKeyType): Promise<Buffer>
}

export type Snapshot = {
	cachedSnapshot?: CachedSnapshot
	storeMetadata?: StoreMetadata
}

export type CachedSnapshot = {
	cachedData: any
	cachedDataSize: number
}

export type MaybeCachedSnapshot = {
	cachedSnapshot?: CachedSnapshot
	skipRemoteCacheSet?: boolean
}

export type StoreMetadata = {
	useLocal: boolean
	useRemote: boolean
}
